import PropTypes from "prop-types"
import React, { useCallback, useState, useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  ChevronDown,
  X,
  Menu,
  Info,
  Phone,
} from "lucide-react"
import { withTranslation } from "react-i18next"
import mbllogo from "../../src/assets/images/new_web_logo.png"
import { SponsoredAdCard } from "./VerticalLayout/SidebarContent"
import SimpleBar from "simplebar-react"
import { usePrefetchOnHover } from "../utils/prefetchManager"

function MobileNav(props) {
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState({})
  const [activeButton, setActiveButton] = useState(null)
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const [username, setUsername] = useState('')
  const [hasTutorialOverlay, setHasTutorialOverlay] = useState(false)
  const path = useLocation()
  const navigate = useNavigate()

  // Prefetch hooks for different routes
  const dashboardPrefetch = usePrefetchOnHover('/dashboard')
  const trendingPrefetch = usePrefetchOnHover('/trending')
  const newReleasePrefetch = usePrefetchOnHover('/new-releases')
  const topAppsPrefetch = usePrefetchOnHover('/top-apps')
  const mostPopularPrefetch = usePrefetchOnHover('/most-popular')
  const preOrdersPrefetch = usePrefetchOnHover('/pre-orders')
  const topDiscountPrefetch = usePrefetchOnHover('/top-discounts')

  // Market Research prefetch hooks
  const keywordSearchPrefetch = usePrefetchOnHover('/keyword-analysis')
  const keywordSuggestionsPrefetch = usePrefetchOnHover('/keyword-suggestions')
  const socialMediaComparisonPrefetch = usePrefetchOnHover('/social-media-comparison')
  const categoryAnalysisPrefetch = usePrefetchOnHover('/categoryanalysis')
  const appTrackingPrefetch = usePrefetchOnHover('/app-tracking')
  const keywordTrackingPrefetch = usePrefetchOnHover('/keyword-tracking')
  const categoryTrackingPrefetch = usePrefetchOnHover('/category-tracking')
  const publisherAnalysisPrefetch = usePrefetchOnHover('/publisheranalysis')
  const categorySuggestionsPrefetch = usePrefetchOnHover('/category-explorer')

  // AI Research prefetch hooks
  const chatbotPrefetch = usePrefetchOnHover('/chatbot')

  // Profile prefetch hooks
  const profileSettingsPrefetch = usePrefetchOnHover('/profile/settings')
  const profileBillingPrefetch = usePrefetchOnHover('/profile')

  // Ad Research prefetch hooks
  const creativeInsightsPrefetch = usePrefetchOnHover('/creative-insights')

  const isSettingsPath = [
    "/profile",
    "/profile/settings",
    "/about-us",
    "/faq",
    "/support",
    "/contact-us"
  ].includes(path.pathname)

  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen)
  }

  const toggleMenu = menuKey => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }))
  }

  // Custom logic for mobile nav highlighting
  const highlightActiveMenu = useCallback(() => {
    const pathName = path.pathname.split("?")[0].split("#")[0]
    const normalizedPathName = pathName === "/" ? "/" : pathName.replace(/\/$/, "")

    // Get menu content container
    const menuContent = document.querySelector(".menu-content")
    if (!menuContent) return

    // Remove active class from all menu items and submenu items
    const allLinks = menuContent.querySelectorAll("a")
    const allSubmenuDivs = menuContent.querySelectorAll(".submenu > div")

    allLinks.forEach(link => {
      link.classList.remove("active")
    })

    allSubmenuDivs.forEach(div => {
      div.classList.remove("active")
    })

    // Remove active from menu items too
    const allMenuItems = menuContent.querySelectorAll(".menu-item, .menu-item-expandable")
    allMenuItems.forEach(item => {
      item.classList.remove("active")
    })

    // Find matching menu item
    let matchingLink = null
    for (let i = 0; i < allLinks.length; i++) {
      const link = allLinks[i]
      const href = link.getAttribute("href")

      if (!href || href === "#") continue

      // Extract path from href
      let linkPath = href
      try {
        if (href.startsWith("/")) {
          linkPath = href.split("?")[0].split("#")[0]
        } else if (href.startsWith("http")) {
          const url = new URL(href)
          linkPath = url.pathname
        } else {
          linkPath = href.split("?")[0].split("#")[0]
        }
      } catch (e) {
        linkPath = href.split("?")[0].split("#")[0]
      }

      const normalizedLinkPath = linkPath === "/" ? "/" : linkPath.replace(/\/$/, "")

      if (normalizedPathName === normalizedLinkPath) {
        matchingLink = link
        break
      }
    }

    if (matchingLink) {
      // Add active class to matching link
      matchingLink.classList.add("active")

      // Check if it's a submenu item
      const submenu = matchingLink.closest(".submenu")
      if (submenu) {
        // Remove active from all other submenu items in this submenu
        const allSubmenuItems = submenu.querySelectorAll("div, a")
        allSubmenuItems.forEach(item => {
          item.classList.remove("active")
        })

        // Remove active from all main menu items
        const allMainMenuItems = menuContent.querySelectorAll(".menu-item, .menu-item-expandable")
        allMainMenuItems.forEach(item => {
          item.classList.remove("active")
          const mainLink = item.querySelector("a")
          if (mainLink) {
            mainLink.classList.remove("active")
          }
        })

        // It's a submenu item - add active to this specific item
        // Direct approach: parent div is the direct parent of the link
        const parentDiv = matchingLink.parentElement

        if (parentDiv && parentDiv.tagName === "DIV" && submenu.contains(parentDiv)) {
          parentDiv.classList.add("active")
        } else {
          // Fallback: try to find the div wrapper
          const wrapperDiv = matchingLink.closest(".submenu > div")
          if (wrapperDiv) {
            wrapperDiv.classList.add("active")
          }
        }

        // Find and expand parent expandable menu (but don't highlight it)
        let currentElement = submenu.previousElementSibling
        while (currentElement) {
          if (currentElement.classList && currentElement.classList.contains("menu-item-expandable")) {
            // Expand this menu but remove active class from it
            currentElement.classList.remove("active")
            const expandableLink = currentElement.querySelector("a")
            if (expandableLink) {
              expandableLink.classList.remove("active")
            }

            const menuKey = currentElement.getAttribute("data-menu-key")

            if (menuKey) {
              setExpandedMenus(prev => ({
                ...prev,
                [menuKey]: true
              }))
            }
            break
          }
          currentElement = currentElement.previousElementSibling
        }
      } else {
        // It's a main menu item
        // Check if it's inside menu-item-expandable
        const expandableMenu = matchingLink.closest(".menu-item-expandable")
        if (expandableMenu) {
          // This is an expandable menu main link (like Weekly Insights)
          // Remove active from all submenu items under this expandable menu
          const submenu = expandableMenu.nextElementSibling
          if (submenu && submenu.classList && submenu.classList.contains("submenu")) {
            const allSubmenuItems = submenu.querySelectorAll("div, a")
            allSubmenuItems.forEach(item => {
              item.classList.remove("active")
            })
          }

          // Add active to expandable menu and its link
          expandableMenu.classList.add("active")
          matchingLink.classList.add("active")

          // Also add active to menu-item-content if it exists
          const menuContentDiv = expandableMenu.querySelector(".menu-item-content")
          if (menuContentDiv) {
            menuContentDiv.classList.add("active")
          }

          const menuKey = expandableMenu.getAttribute("data-menu-key")
          if (menuKey) {
            setExpandedMenus(prev => ({
              ...prev,
              [menuKey]: true
            }))
          }
        } else {
          // Regular menu item
          const parentDiv = matchingLink.closest(".menu-item")
          if (parentDiv) {
            parentDiv.classList.add("active")
          }
        }
      }
    }
  }, [path.pathname, setExpandedMenus])

  useEffect(() => {
    // Close panel when path changes
    setIsPanelOpen(false)

    // Delay to ensure DOM is ready and expanded menus are rendered, then highlight
    const timer = setTimeout(() => {
      highlightActiveMenu()
    }, 300)
    return () => clearTimeout(timer)
  }, [highlightActiveMenu, path.pathname])

  // Re-highlight when menus expand to ensure submenu items get highlighted
  useEffect(() => {
    if (Object.values(expandedMenus).some(Boolean)) {
      const timer = setTimeout(() => {
        highlightActiveMenu()
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [expandedMenus, highlightActiveMenu])

  // Keep activeButton only while location matches the button's target route.
  // Clear it when user navigates to a different page.
  useEffect(() => {
    if (!activeButton) return
    const map = {
      discount: '/top-discounts',
      trending: '/trending',
      newRelease: '/new-releases',
    }
    const target = map[activeButton]
    if (!target) return
    const pathname = (path && path.pathname) || ''
    if (pathname === target || pathname.startsWith(target + '/')) {
      return
    }
    setActiveButton(null)
  }, [path && path.pathname, activeButton])

  // Watch DOM for global tutorial backdrops and visually blur the sidebar while active
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return

    const checkOverlay = () => {
      try {
        const hasOverlay = !!document.querySelector(".kt-backdrop-overlay, .profile-tutorial-backdrop-overlay")
        setHasTutorialOverlay(hasOverlay)
      } catch (e) {
        // ignore
      }
    }

    // Initial check
    checkOverlay()

    // Observe DOM mutations to react when overlays are added/removed
    const observer = new MutationObserver(() => {
      checkOverlay()
    })

    try {
      observer.observe(document.body, { childList: true, subtree: true })
    } catch (e) {
      // ignore observe errors
    }

    // Also listen to keyword tutorial custom events as an extra signal
    window.addEventListener("keywordTutorialStep", checkOverlay)
    window.addEventListener("skipAllKeywordTutorial", checkOverlay)

    return () => {
      observer.disconnect()
      window.removeEventListener("keywordTutorialStep", checkOverlay)
      window.removeEventListener("skipAllKeywordTutorial", checkOverlay)
    }
  }, [])

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Get username from storage
  useEffect(() => {
    // try different storage keys and authUser object
    const tryKeys = ['userName', 'username', 'user_fullname', 'fullName', 'name', 'displayName', 'user']
    for (const k of tryKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k)
      if (v) {
        setUsername(v)
        return
      }
    }
    const authUserRaw = localStorage.getItem('authUser') || sessionStorage.getItem('authUser')
    if (authUserRaw) {
      try {
        const obj = JSON.parse(authUserRaw)
        const candidate = obj.displayName || obj.username || obj.name || obj.fullName || obj.email || ''
        if (candidate) setUsername(candidate)
      } catch (e) {
        // ignore
      }
    }
  }, [])

  // Responsive button style - same as MobileHeader.jsx
  const isMobile = windowWidth < 768
  const isSmallMobile = windowWidth < 480
  const isVerySmallMobile = windowWidth < 360
  const mobileNavButtonStyle = {
    background: 'hsla(0, 32%, 89%, 0.04)',
    color: '#E7E9EF',
    border: '1px solid rgba(255,255,255,0.06)',
    padding: isVerySmallMobile ? '3px 4px' : isSmallMobile ? '4px 4px' : isMobile ? '4px 5px' : '6px 7px',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: isVerySmallMobile ? 9 : isSmallMobile ? 10 : isMobile ? 11 : 12,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition: 'background 150ms, color 150ms, transform 120ms',
    minWidth: 'fit-content',
    width: 'auto',
    maxWidth: 'none',
    overflow: 'visible',
    textOverflow: 'clip',
  }

  const mobileActiveStyle = {
    background: '#ffffff',
    color: '#1F2230',
    border: '1px solid rgba(0,0,0,0.08)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  }

  return (
    <>
      <div className="custom-mobile-nav-wrapper hide-me-on-desktop">
        <div className="mobile-nav-container">
          {/* Menu Button - toggles between Menu and X icon */}
          <button
            onClick={togglePanel}
            className="menu-button"
            aria-label={isPanelOpen ? "Close menu" : "Open menu"}
          >
            {isPanelOpen ? (
              <X size={24} color="#E7E9EF" />
            ) : (
              <Menu size={24} color="#E7E9EF" />
            )}
          </button>

          {/* Overlay */}
          {isPanelOpen && <div className="overlay" onClick={togglePanel} />}

          {/* Side Panel */}
          <div className={`side-panel ${isPanelOpen ? "open" : ""}`}>
            <div className="panel-header">
              <div className="logo-container">
                <span>
                  <img
                    src={mbllogo}
                    alt="KeyQuest Logo"
                    style={{
                      height: isVerySmallMobile ? '28px' : isSmallMobile ? '34px' : '38px',
                      width: 'auto',
                      maxWidth: isVerySmallMobile ? '120px' : isSmallMobile ? '140px' : '160px',
                      objectFit: 'contain'
                    }}
                  />
                </span>
              </div>
            </div>

            {/* Menu Content */}
            <SimpleBar
              style={{
                flex: 1,
                minHeight: 0,
                // Slight blur for visual focus when any tutorial overlay is active
                filter: hasTutorialOverlay ? "blur(3px)" : "none",
                // Also make the sidebar non-interactive while tutorial is active
                pointerEvents: hasTutorialOverlay ? "none" : "auto",
              }}
            >
              <div className="menu-content">
              {/* Username Greeting */}
              {username && (
                <div style={{
                  padding: '8px 20px 12px 20px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  marginBottom: '0px'
                }}>
                  <div style={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: isVerySmallMobile ? 14 : isSmallMobile ? 15 : 16,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    Hi, {username}
                  </div>
                </div>
              )}

              {isSettingsPath ? (
                <div className="menu-section" style={{ marginTop: '8px' }}>
                  <div className="menu-title">
                    <span style={{
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      color: 'var(--bs-sidebar-menu-item-icon-color)',
                      fontWeight: '600',
                      letterSpacing: '0.05em',
                      display: 'block',
                      opacity: 0.7,
                      textAlign: 'left',
                      paddingLeft: '20px !important',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      paddingRight: '20px',
                      margin: 0
                    }}>
                      {props.t("Settings")}
                    </span>
                  </div>
                  <div className="menu-item" style={{ marginTop: '-4px' }}>
                    <Link 
                      to="/profile/settings" 
                      className="waves-effect"
                      {...profileSettingsPrefetch}
                      onClick={() => setIsPanelOpen(false)}
                    >
                      <i className="uil-user-circle"></i>
                      <span>{props.t("Profile")}</span>
                    </Link>
                  </div>
                  <div className="menu-item" style={{ marginTop: '-4px' }}>
                    <Link 
                      to="/profile" 
                      className="waves-effect"
                      {...profileBillingPrefetch}
                      onClick={() => setIsPanelOpen(false)}
                    >
                      <i className="uil-invoice"></i>
                      <span>{props.t("Billings")}</span>
                    </Link>
                  </div>
                  <div className="menu-item" style={{ marginTop: '-4px' }}>
                    <Link 
                      to="/about-us" 
                      className="waves-effect"
                      onClick={() => setIsPanelOpen(false)}
                    >
                      <Info size={16} />
                      <span>{props.t("About Us")}</span>
                    </Link>
                  </div>
                  <div className="menu-item" style={{ marginTop: '-4px' }}>
                    <Link 
                      to="/faq" 
                      className="waves-effect"
                      onClick={() => setIsPanelOpen(false)}
                    >
                      <i className="uil-question-circle"></i>
                      <span>{props.t("FAQ")}</span>
                    </Link>
                  </div>
                  <div className="menu-item" style={{ marginTop: '-4px' }}>
                    <Link 
                      to="/support" 
                      className="waves-effect"
                      onClick={() => setIsPanelOpen(false)}
                    >
                      <i className="uil-life-ring"></i>
                      <span>{props.t("Support")}</span>
                    </Link>
                  </div>
                  <div className="menu-item" style={{ marginTop: '-4px' }}>
                    <Link 
                      to="/contact-us" 
                      className="waves-effect"
                      onClick={() => setIsPanelOpen(false)}
                    >
                      <Phone size={16} />
                      <span>{props.t("Contact Us")}</span>
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  {/* Quick Navigation Buttons */}
                  <div className="menu-section" style={{ marginTop: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isVerySmallMobile ? 2 : isSmallMobile ? 3 : isMobile ? 4 : 5,
                      padding: isVerySmallMobile ? '12px 8px' : '12px 8px',
                      flexWrap: isVerySmallMobile ? 'wrap' : 'nowrap',
                      overflowX: isVerySmallMobile ? 'visible' : 'auto',
                      overflowY: 'hidden',
                      WebkitOverflowScrolling: 'touch',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      marginBottom: '8px',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0
                    }}>
                      <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                        <button
                          type="button"
                          aria-label="Discount"
                          onClick={() => {
                            setActiveButton('discount')
                            navigate('/top-discounts')
                            setIsPanelOpen(false)
                          }}
                          style={{ ...mobileNavButtonStyle, ...(activeButton === 'discount' ? mobileActiveStyle : {}) }}
                        >
                          Top Discount Apps
                        </button>
                      </div>
                      <button
                        type="button"
                        aria-label="Trending"
                        onClick={() => {
                          setActiveButton('trending')
                          navigate('/trending')
                          setIsPanelOpen(false)
                        }}
                        style={{ ...mobileNavButtonStyle, ...(activeButton === 'trending' ? mobileActiveStyle : {}) }}
                      >
                        Trending Apps
                      </button>
                      <button
                        type="button"
                        aria-label="New Release"
                        onClick={() => {
                          setActiveButton('newRelease')
                          navigate('/new-releases')
                          setIsPanelOpen(false)
                        }}
                        style={{ ...mobileNavButtonStyle, ...(activeButton === 'newRelease' ? mobileActiveStyle : {}) }}
                      >
                        New Release Apps
                      </button>
                    </div>
                  </div>

                  {/* Main Navigation */}
                  <div className="menu-section" style={{ marginTop: '8px' }}>
                    <div className="menu-item" style={{ marginTop: '-4px' }}>
                      <Link
                        to="/dashboard"
                        className="waves-effect"
                        title={props.t("Home")}
                        {...dashboardPrefetch}
                        onClick={() => setIsPanelOpen(false)}
                      >
                        <i className="uil uil-home-alt"></i>
                        <span>{props.t("Home")}</span>
                      </Link>
                    </div>

                    <div className="menu-item" style={{ marginTop: '-4px' }}>
                      <Link
                        to="/blog"
                        className="waves-effect"
                        title={props.t("Blog")}
                        onClick={() => setIsPanelOpen(false)}
                      >
                        <i className="uil uil-book-alt"></i>
                        <span>{props.t("Blog")}</span>
                      </Link>
                    </div>

                    <div className="menu-item" style={{ marginTop: '-4px' }}>
                      <Link
                        to="/ai-cmo"
                        className="waves-effect"
                        onClick={() => setIsPanelOpen(false)}
                      >
                        <i className="uil-search"></i>
                        <span>{props.t("AI CMO")}</span>
                      </Link>
                    </div>

                    <div className="menu-item" style={{ marginTop: '-4px' }}>
                      <Link
                        to="/store-validator"
                        className="waves-effect"
                        onClick={() => setIsPanelOpen(false)}
                      >
                        <i className="uil-shield-check"></i>
                        <span>{props.t("Store Validator")}</span>
                      </Link>
                    </div>

                    {/* Market Research Section */}
                  <div className="menu-section" style={{ marginTop: '2px' }}>
                      <div className="menu-title">
                        <span style={{
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          color: 'var(--bs-sidebar-menu-item-icon-color)',
                          fontWeight: '600',
                          letterSpacing: '0.05em',
                          display: 'block',
                          opacity: 0.7,
                          textAlign: 'left',
                          paddingLeft: '20px !important',
                          paddingTop: '2px',
                          paddingBottom: '2px',
                          paddingRight: '20px',
                          margin: 0
                        }}>
                          {props.t("Market Research")}
                        </span>
                      </div>

                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/keyword-suggestions"
                          className="waves-effect"
                          title={props.t("Keyword Suggestions")}
                          {...keywordSuggestionsPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-lightbulb"></i>
                          <span>{props.t("Keyword Suggestions")}</span>
                        </Link>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/category-explorer"
                          className="waves-effect"
                          title={props.t("Category Explorer")}
                          {...categorySuggestionsPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-lightbulb"></i>
                          <span>{props.t("Category Explorer")}</span>
                        </Link>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/keyword-analysis"
                          className="waves-effect"
                          title={props.t("Keyword Explorer")}
                          {...keywordSearchPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-search"></i>
                          <span>{props.t("Keyword Explorer")}</span>
                        </Link>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/category-analysis"
                          className="waves-effect"
                          {...categoryAnalysisPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-layers"></i>
                          <span>{props.t("Genre Explorer")}</span>
                        </Link>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/publisher-analysis"
                          className="waves-effect"
                          {...publisherAnalysisPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-chart-line"></i>
                          <span>{props.t("Publisher Explorer")}</span>
                        </Link>
                      </div>
                    </div>

                    {/* Brand Research Section */}
                    <div className="menu-section" style={{ marginTop: '2px' }}>
                      <div className="menu-title">
                        <span style={{
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          color: 'var(--bs-sidebar-menu-item-icon-color)',
                          fontWeight: '600',
                          letterSpacing: '0.05em',
                          display: 'block',
                          opacity: 0.7,
                          textAlign: 'left',
                          paddingLeft: '20px !important',
                          paddingTop: '2px',
                          paddingBottom: '2px',
                          paddingRight: '20px',
                          margin: 0
                        }}>
                          {props.t("Brand Research")}
                        </span>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/social-media-comparison"
                          className="waves-effect"
                          title={props.t("Social Comparison")}
                          {...socialMediaComparisonPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-share-alt"></i>
                          <span>{props.t("Social Comparison")}</span>
                        </Link>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/ugc-explorer"
                          className="waves-effect"
                          title={props.t("UGC Explorer")}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-video"></i>
                          <span>{props.t("UGC Explorer")}</span>
                        </Link>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/influencer-explorer"
                          className="waves-effect"
                          title={props.t("Influencer Explorer")}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-users-alt"></i>
                          <span>{props.t("Influencer Explorer")}</span>
                        </Link>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/creative-insights"
                          className="waves-effect"
                          {...creativeInsightsPrefetch}
                          onClick={() => {
                            setIsPanelOpen(false)
                            navigate('/creative-insights')
                          }}
                        >
                          <i className="uil uil-trophy"></i>
                          <span>{props.t("Ad Discovery")}</span>
                        </Link>
                      </div>
                    </div>

                    {/* Tracking & Performance Section */}
                    <div className="menu-section" style={{ marginTop: '2px' }}>
                      <div className="menu-title">
                        <span style={{
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          color: 'var(--bs-sidebar-menu-item-icon-color)',
                          fontWeight: '600',
                          letterSpacing: '0.05em',
                          display: 'block',
                          opacity: 0.7,
                          textAlign: 'left',
                          paddingLeft: '20px !important',
                          paddingTop: '2px',
                          paddingBottom: '2px',
                          paddingRight: '20px',
                          margin: 0
                        }}>
                          {props.t("TRACKING & PERFORMANCE")}
                        </span>
                      </div>
                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/app-tracking"
                          className="waves-effect"
                          {...appTrackingPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-chart-growth"></i>
                          <span>{props.t("App Tracking")}</span>
                        </Link>
                      </div>

                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/keyword-tracking"
                          className="waves-effect"
                          {...keywordTrackingPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-crosshair"></i>
                          <span>{props.t("Keyword Tracking")}</span>
                        </Link>
                      </div>

                      <div className="menu-item" style={{ marginTop: '-4px' }}>
                        <Link
                          to="/category-tracking"
                          className="waves-effect"
                          {...categoryTrackingPrefetch}
                          onClick={() => setIsPanelOpen(false)}
                        >
                          <i className="uil-layer-group"></i>
                          <span>{props.t("Genre Tracking")}</span>
                        </Link>
                      </div>
                    </div>

                    {/* INSIGHTS Section */}
                    <div className="menu-section" style={{ marginTop: '2px' }}>
                      <div className="menu-title">
                        <span style={{
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        color: 'var(--bs-sidebar-menu-item-icon-color)',
                        fontWeight: '600',
                        letterSpacing: '0.05em',
                        display: 'block',
                        opacity: 0.7,
                        textAlign: 'left',
                        paddingLeft: '20px !important',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        paddingRight: '20px',
                        margin: 0
                      }}>
                        {props.t("INSIGHTS")}
                      </span>
                    </div>
                    <div
                      className="menu-item-expandable"
                      data-menu-key="weekly"
                      onClick={(e) => {
                        // Only toggle if clicking on the expandable area, not on the Link
                        if (e.target.closest("a")) {
                          return // Link will handle its own click
                        }
                        // Remove active from all submenu items
                        const menuContent = document.querySelector(".menu-content")
                        if (menuContent) {
                          const weeklySubmenu = menuContent.querySelector('[data-menu-key="weekly"]')?.nextElementSibling
                          if (weeklySubmenu && weeklySubmenu.classList.contains("submenu")) {
                            const allSubmenuItems = weeklySubmenu.querySelectorAll("div, a")
                            allSubmenuItems.forEach(item => item.classList.remove("active"))
                          }
                        }
                        toggleMenu("weekly")
                      }}
                    >
                      <div className="menu-item-content">
                        <Link
                          to="/weekly-insights"
                          className="has-arrow waves-effect"
                          onClick={e => {
                            // Remove active from all items
                            const menuContent = document.querySelector(".menu-content")
                            if (menuContent) {
                              const allLinks = menuContent.querySelectorAll("a")
                              const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                              allLinks.forEach(link => link.classList.remove("active"))
                              allDivs.forEach(div => div.classList.remove("active"))
                            }

                            // prevent default to avoid any native toggles interfering
                            e.preventDefault()
                            // navigate using withRouter injected navigate to ensure SPA routing
                            if (props && props.router && props.router.navigate) {
                              props.router.navigate('/weekly-insights')
                            } else {
                              // fallback
                              window.location.href = '/weekly-insights'
                            }

                            // Highlight after navigation
                            setTimeout(() => highlightActiveMenu(), 200)
                          }}
                        >
                          <i className="uil-lightbulb-alt"></i>
                          <span className="">{"Weekly Insights"}</span>
                        </Link>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`chevron-icon ${expandedMenus.weekly ? "rotated" : ""
                          }`}
                      />
                    </div>

                    {expandedMenus.weekly && (
                      <div className="submenu">
                        <div>
                        <Link
                          to="/trending"
                          onClick={() => {
                            setActiveButton('trending')
                            navigate('/trending')
                            setIsPanelOpen(false)
                          }}
                          {...trendingPrefetch}
                        >
                          {props.t("Trending")}
                        </Link>
                        </div>

                        <div>
                        <Link
                          to="/new-releases"
                          onClick={() => {
                            setActiveButton('newRelease')
                            navigate('/new-releases')
                            setIsPanelOpen(false)
                          }}
                          {...newReleasePrefetch}
                        >
                          {props.t("New Release")}
                        </Link>
                        </div>

                        <div>
                        <Link
                          to="/top-apps"
                          onClick={() => {
                            navigate('/top-apps')
                            setIsPanelOpen(false)
                          }}
                          {...topAppsPrefetch}
                        >
                          {props.t("Top Selling")}
                        </Link>
                        </div>

                        <div>
                        <Link
                          to="/most-popular"
                          onClick={() => {
                            navigate('/most-popular')
                            setIsPanelOpen(false)
                          }}
                          {...mostPopularPrefetch}
                        >
                          {props.t("Popular")}
                        </Link>
                        </div>

                        <div>
                        <Link
                          to="/top-discounts"
                          onClick={() => {
                            setActiveButton('discount')
                            navigate('/top-discounts')
                            setIsPanelOpen(false)
                          }}
                          {...topDiscountPrefetch}
                        >
                          {props.t("Top Discounts")}
                        </Link>
                        </div>

                        <div>
                          <Link
                            to="/pre-orders"
                            onClick={() => {
                              navigate('/pre-orders')
                              setIsPanelOpen(false)
                            }}
                            {...preOrdersPrefetch}
                          >
                            {props.t("PreOrders")}
                          </Link>
                        </div>
                      </div>
                    )}

                    <div
                      className="menu-item-expandable"
                      data-menu-key="custom"
                      onClick={() => {
                        // Remove active from all items
                        const menuContent = document.querySelector(".menu-content")
                        if (menuContent) {
                          const allLinks = menuContent.querySelectorAll("a")
                          const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                          allLinks.forEach(link => link.classList.remove("active"))
                          allDivs.forEach(div => div.classList.remove("active"))
                        }
                        toggleMenu("custom")
                        // Highlight after toggle
                        setTimeout(() => {
                          const menuContent = document.querySelector(".menu-content")
                          if (menuContent) {
                            const customMenu = menuContent.querySelector('[data-menu-key="custom"]')
                            if (customMenu) {
                              customMenu.classList.add("active")
                            }
                          }
                        }, 100)
                      }}
                    >
                      <div className="menu-item-content">
                        <Link
                          to="/custom-insights"
                          className="has-arrow waves-effect"
                          onClick={e => {
                            // Remove active from all items
                            const menuContent = document.querySelector(".menu-content")
                            if (menuContent) {
                              const allLinks = menuContent.querySelectorAll("a")
                              const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                              allLinks.forEach(link => link.classList.remove("active"))
                              allDivs.forEach(div => div.classList.remove("active"))
                            }

                            // prevent default to avoid any native toggles interfering
                            e.preventDefault()
                            // navigate using withRouter injected navigate to ensure SPA routing
                            if (props && props.router && props.router.navigate) {
                              props.router.navigate('/custom-insights')
                            } else {
                              // fallback
                              window.location.href = '/custom-insights'
                            }

                            // Highlight after navigation
                            setTimeout(() => highlightActiveMenu(), 200)
                          }}
                        >
                          <i className="uil-analytics"></i>
                          <span className="">{"Custom Insights"}</span>
                        </Link>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`chevron-icon ${expandedMenus.custom ? "rotated" : ""
                          }`}
                      />
                    </div>

                    {expandedMenus.custom && (
                      <div className="submenu">
                        <div>
                          <Link
                            to="/top-genres-2025"
                            onClick={(e) => {
                              const menuContent = document.querySelector(".menu-content")
                              if (menuContent) {
                                const allLinks = menuContent.querySelectorAll("a")
                                const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                                allLinks.forEach(link => link.classList.remove("active"))
                                allDivs.forEach(div => div.classList.remove("active"))
                              }
                              setTimeout(() => highlightActiveMenu(), 200)
                            }}
                          >
                            {props.t("Top Genres of 2025")}
                          </Link>
                        </div>
                        <div>
                          <Link
                            to="/top-revenued"
                            onClick={(e) => {
                              const menuContent = document.querySelector(".menu-content")
                              if (menuContent) {
                                const allLinks = menuContent.querySelectorAll("a")
                                const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                                allLinks.forEach(link => link.classList.remove("active"))
                                allDivs.forEach(div => div.classList.remove("active"))
                              }
                              setTimeout(() => highlightActiveMenu(), 200)
                            }}
                          >
                            {props.t("Top Revenued")}
                            <span className="badge rounded-pill float-end">
                              {" "}
                              {/* {props.t("New")}{" "} */}
                            </span>
                          </Link>
                        </div>
                        <div>
                          <Link
                            to="/top-downloaded"
                            onClick={(e) => {
                              const menuContent = document.querySelector(".menu-content")
                              if (menuContent) {
                                const allLinks = menuContent.querySelectorAll("a")
                                const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                                allLinks.forEach(link => link.classList.remove("active"))
                                allDivs.forEach(div => div.classList.remove("active"))
                              }
                              setTimeout(() => highlightActiveMenu(), 200)
                            }}
                          >
                            {props.t("Top Downloads")}
                            <span className="badge rounded-pill float-end">
                              {" "}
                              {/* {props.t("New")}{" "} */}
                            </span>
                          </Link>
                        </div>
                        <div>
                          <Link
                            to="/top-rated"
                            onClick={(e) => {
                              const menuContent = document.querySelector(".menu-content")
                              if (menuContent) {
                                const allLinks = menuContent.querySelectorAll("a")
                                const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                                allLinks.forEach(link => link.classList.remove("active"))
                                allDivs.forEach(div => div.classList.remove("active"))
                              }
                              setTimeout(() => highlightActiveMenu(), 200)
                            }}
                          >
                            {props.t("Top Average Rating")}
                            <span className="badge rounded-pill float-end">
                              {" "}
                              {/* {props.t("New")}{" "} */}
                            </span>
                          </Link>
                        </div>
                        <div>
                          <Link
                            to="/all-time-hits"
                            onClick={(e) => {
                              const menuContent = document.querySelector(".menu-content")
                              if (menuContent) {
                                const allLinks = menuContent.querySelectorAll("a")
                                const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                                allLinks.forEach(link => link.classList.remove("active"))
                                allDivs.forEach(div => div.classList.remove("active"))
                              }
                              setTimeout(() => highlightActiveMenu(), 200)
                            }}
                          >
                            {props.t("Top App Score")}
                          </Link>
                        </div>
                        {/* <div>
                          <Link
                            to="/top-keywords-categories"
                            onClick={(e) => {
                              const menuContent = document.querySelector(".menu-content")
                              if (menuContent) {
                                const allLinks = menuContent.querySelectorAll("a")
                                const allDivs = menuContent.querySelectorAll(".menu-item, .menu-item-expandable, .submenu > div")
                                allLinks.forEach(link => link.classList.remove("active"))
                                allDivs.forEach(div => div.classList.remove("active"))
                              }
                              setTimeout(() => highlightActiveMenu(), 200)
                            }}
                          >
                            {props.t("Top Keywords & Categories")}
                            <span className="badge rounded-pill float-end">
                              {" "}
                              {props.t("New")}{" "}
                            </span>
                          </Link>
                        </div> */}

                      </div>
                    )}
                    </div>
                  {/* AI Research Section */}
                  <div className="menu-section" style={{ marginTop: '2px' }}>
                    <div className="menu-title">
                    <span style={{
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      color: 'var(--bs-sidebar-menu-item-icon-color)',
                      fontWeight: '600',
                      letterSpacing: '0.05em',
                      display: 'block',
                      opacity: 0.7,
                      textAlign: 'left',
                      paddingLeft: '20px !important',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      paddingRight: '20px',
                      margin: 0
                    }}>
                      {props.t("AI Research")}
                    </span>
                  </div>

                  <div className="menu-item" style={{ marginTop: '-4px' }}>
                    <Link 
                      to="/chatbot" 
                      className="waves-effect"
                      {...chatbotPrefetch}
                      onClick={() => setIsPanelOpen(false)}
                    >
                      <i className="uil-comments-alt"></i>
                      <span>{props.t("AI Chat")}</span>
                    </Link>
                  </div>
                  </div>
                </div>
                </>
              )}
              </div>
            </SimpleBar>

            {/* Fixed Sponsored Ad Card at Bottom — a flex-shrink:0 sibling of the
                SimpleBar above, inside `.side-panel`, which was never scrollable
                itself (no `overflow-y: auto` on it), so this just takes its own
                height and sits pinned at the bottom. */}
            <div
              style={{
                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                paddingTop: '0px',
                paddingBottom: '2px',
                flexShrink: 0,
                backgroundColor: '#171821',
                overflow: 'hidden'
              }}>
              <SponsoredAdCard variant="mobile" />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
MobileNav.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withTranslation()(MobileNav)
